import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["tracks", "isPlaying", "onEnd", "onClick", "children", "data-id"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import PropTypes from 'prop-types';
import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useEventListener } from '../../hooks';
function LinkedList(list) {
  this.head = null;
  this.tail = null;
  list.forEach(value => {
    const newNode = {
      value,
      next: null
    };
    if (!this.head) this.head = this.tail = newNode;else {
      const prevTail = this.tail;
      this.tail = prevTail.next = newNode;
    }
  });
}
function assertTracks(tracks) {
  if (tracks.length && tracks.every(el => el.url)) return;
  throw RangeError('The tracks array is empty or a track url is missing.');
}
function getTrackList(tracks) {
  return new LinkedList(tracks);
}
export const MusicManager = memo(forwardRef(function MusicManager(_ref, ref) {
  let {
      tracks,
      isPlaying,
      onEnd,
      onClick,
      children,
      'data-id': dataId
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  assertTracks(tracks);
  const audioRef = useRef();
  const trackList = useMemo(() => getTrackList(tracks), [tracks]);
  const [audio, setAudio] = useState(trackList.head);
  const [trigger, setTrigger] = useState(null);
  useImperativeHandle(ref, () => trigger, [trigger]);
  const triggerRef = useCallback(node => {
    if (node !== null) setTrigger(node);
  }, []);
  const handleEnd = () => {
    if (audio.next) return setAudio(audio.next);
    if (onEnd) onEnd(dataId);
    setAudio(trackList.head);
  };
  const load = useCallback(() => audioRef.current.load(), []);
  const clickHandler = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    return onClick === null || onClick === void 0 ? void 0 : onClick(dataId);
  }, [onClick, dataId]);
  useEventListener('ended', handleEnd, audioRef.current);
  useEventListener('mousedown', load, trigger, {
    once: true
  });
  useEventListener('touchstart', load, trigger, {
    once: true
  });
  useEffect(() => void setAudio(trackList.head), [trackList]);
  useEffect(() => {
    const track = audioRef.current;
    if (!track) return;
    if (isPlaying) track.play();
    if (!isPlaying) track.pause();
  }, [isPlaying, audio]);
  const {
    url,
    name
  } = audio.value;
  return React.createElement(React.Fragment, null, children(_objectSpread({
    triggerRef,
    clickHandler
  }, props)), React.createElement("audio", {
    src: url,
    preload: "auto",
    ref: audioRef,
    label: name,
    title: name
  }, React.createElement("p", null, "Your browser does not implement the audio element.")));
}));
MusicManager.propTypes = process.env.NODE_ENV !== "production" ? {
  'data-id': PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  tracks: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
    name: PropTypes.string
  })).isRequired,
  isPlaying: PropTypes.bool,
  onClick: PropTypes.func,
  onEnd: PropTypes.func,
  children: PropTypes.func.isRequired
} : {};
MusicManager.defaultProps = {
  isPlaying: false,
  onClick: null,
  onEnd: null
};